import {Component, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {BehaviorSubject, Subscription} from 'rxjs';
import {SortTable} from '../../../../../shared/models/sort-table/types';
import {App, AppId} from '../../../../../shared/models/app';
import {Store} from '@ngrx/store';
import {AppState} from '../../../../../app/app.state';
import {AppsService} from '../../../../../core/services/apps.service';
import {UrlService} from '../../../../../core/services/url.service';
import {AppsState} from '../../../../../core/state/apps.state';
import {TitleBreadcrumbsChangedAction} from '../../../../../core/actions/title.actions';
import {SortTableCellTextPill, SortTableCellKebabMenu} from '../../../../../shared/models/sort-table/cell-classes';
import {ShadeToolsService} from '../../../../../core/services/shade-tools.service';
import {OptionGroup} from '../../../../../shared/models/option';
import {SearchService} from '../../../../../core/services/search.service';
import {AppGlobalSettingsComponent} from '../../../../components/app-global-settings/app-global-settings.component';
import {PopoverMenuComponent} from '../../../../components/popover-menu/popover-menu.component';
import {SortTableComponent} from '../../../../components/sort-table/sort-table.component';
import { DialogBoxButtonColor } from 'src/shared/models/dialog-box';

@Component({
  selector: 'motn-manage-units-prop-manager',
  templateUrl: './manage-units-prop-manager.component.html',
  styleUrls: ['./manage-units-prop-manager.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AppGlobalSettingsComponent,
    PopoverMenuComponent,
    SortTableComponent,
  ],
})
export class ManageUnitsPropManagerComponent implements OnInit, OnDestroy {
  app: App;
  private appId: AppId;

  searchTerm: string = '';
  addUserMenuOptions: OptionGroup;

  private childApps: App[];

  table$ = new BehaviorSubject<SortTable>(null);

  private appsSub: Subscription;
  private searchSub: Subscription;


  constructor(
    private store: Store<AppState>,
    private appsService: AppsService,
    private shadeTools: ShadeToolsService,
    private searchService: SearchService,
    private urlService: UrlService,
  ) {
  }


  ngOnInit(): void {
    this.appId = this.appsService.currentAppId;
    this.appsService.fetchApp(this.appId);
    this.searchService.enableSearch();

    this.searchSub = this.urlService.searchTerm$.subscribe((term) => {
      this.searchTerm = term;
      this.updateTable();
    });

    this.appsSub = this.store.select('apps').subscribe(
      (data: AppsState) => {
        this.app = data.apps.find((a) => {
          return a.ID === this.appId;
        });

        if (this.app) {
          this.updateTitle();

          if (!this.childApps) {
            this.childApps = [];
            this.updateChildApps();
          }
        }
      },
    );
  }


  ngOnDestroy(): void {
    this.appsSub?.unsubscribe();
    this.table$?.complete();
  }


  private updateChildApps() {
    this.appsService.fetchChildApps$(this.appId).subscribe(
      (childApps) => {
        this.childApps = childApps;
        this.updateTable();
      },
    );
  }


  private showError() {
    this.shadeTools.errorDialog();
  }


  private updateTable() {
    if (!this.childApps) {
      return;
    }

    const table: SortTable = {
      css: {
        'min-width': '200px',
      },
      columns: [
        {
          name: 'Tenant Unit',
          css: {
            'width': 'calc(100% - 40px)',
          },
          sortingFn: SortTableCellTextPill.sortingFnMainText,
          sortDefault: 'up',
        },
        {
          name: '',
          css: {
            'width': '40px',
            'justify-content': 'right',
          },
        },
      ],
      rows: [],
      pagination: {
        enabled: true,
        rowsPerPage: 15,
        startOnPage: 1,
        maxPageOptions: 10,
      },
    };

    let childApps = this.childApps;

    if (this.searchTerm) {
      childApps = this.searchService.searchApps(this.searchTerm, childApps);
    }

    for (let childApp of childApps) {
      table.rows.push({
        context: '' + childApp.ID,
        cells: [
          new SortTableCellTextPill(childApp.Name, ''),
          new SortTableCellKebabMenu(childApp.ID, [
            {
              id: '0',
              name: 'Open Tenant Portal',
              href: UrlService.makeTenantPortalDashboardUrl(childApp.ID),
            },
            {
              id: '1',
              name: 'Add/Remove Users',
              href: UrlService.makePropManagerManageUnitUsersUrl(this.appId, childApp.ID),
            },
            {
              id: '2',
              name: 'Configure',
              href: UrlService.makePropManagerManageUnitConfigurationUrl(this.appId, childApp.ID),
            },
            {
              id: '3',
              name: 'Delete Unit',
              callback: () => {
                this.shadeTools.startDialog(
                  `Delete Unit`,
                  `Are you sure you want to delete ${childApp.Name}? To confirm, type DELETE in the box below and confirm.`,
                  [
                    {
                      text: 'Confirm',
                      callback: (input: string) => {
                        if (input !== 'DELETE') {
                          this.shadeTools.errorDialog(
                            `The word DELETE must be typed exactly for deletion to occur. Please try again.`,
                          );
                          return;
                        }

                        this.appsService.deleteApp$(childApp.ID).subscribe(
                          () => {
                            this.shadeTools.closeAll();
                            this.updateChildApps();
                          },
                          () => {
                            this.showError();
                          },
                        );
                      },
                      color: DialogBoxButtonColor.Red,
                    },
                    {
                      text: 'Cancel',
                      callback: () => {
                        this.shadeTools.closeAll();
                      },
                      color: DialogBoxButtonColor.Alt,
                    },
                  ],
                  true,
                  false,
                  false,
                  false,
                );
              },
            },
          ], '', false, 4),
        ],
      });
    }

    this.searchService.setResultQty(table.rows.length);
    this.table$.next(table);
  }


  private updateTitle() {
    this.store.dispatch(new TitleBreadcrumbsChangedAction(
      [{
        path: UrlService.makePropManagerManageUnitsUrl(this.appId),
        text: 'Property Manager',
      }, {
        path: UrlService.makePropManagerManageUnitsUrl(this.appId),
        text: 'Manage Units',
      }],
    ));
  }


  openAddUnitMenu() {
    this.addUserMenuOptions = new OptionGroup([
      {
        id: '0',
        name: 'Create New Unit',
        callback: () => {
          this.shadeTools.startDialog(
            `New Tenant Unit`,
            `Provide a name for the new unit. It can be changed later as well.`,
            [
              {
                text: 'Create Unit',
                callback: (input: string) => {
                  this.appsService.createChildApp$(this.appId, {
                    Owner: this.app.Owner,
                    Name: input.trim(),
                    Image: this.app.Image,
                    Type: 'tenant-portal',
                    Config: {
                      Subtitle: {
                        Text: this.app.Name,
                      },
                      Location: {
                        PostalCode: this.app.Config?.Location?.PostalCode,
                      },
                      Template: {
                        Type: 'occupant-tenant',
                        Modules: {},
                      },
                    },
                  }).subscribe(
                    () => {
                      this.shadeTools.closeAll();
                      this.updateChildApps();
                    },
                    () => {
                      this.showError();
                    },
                  );
                },
                callbackIsDefault: true,
                color: DialogBoxButtonColor.Green,
              },
              {
                text: 'Cancel',
                callback: () => {
                  this.shadeTools.closeAll();
                },
                color: DialogBoxButtonColor.Alt,
              },
            ],
            true,
            false,
            false,
            false,
          );
        },
      },
      // {
      //   id: '1',
      //   name: 'From Existing Unit',
      // },
    ]);
  }


  closeAddUserMenu() {
    this.addUserMenuOptions = null;
  }

}
